// Marks of other companies on the device models, and how the room leaves
// them out.
//
// The models show real consoles, and three of them came with the logos and
// wordmarks the real ones carry: the PS logo, SONY, PS VITA, PSP, UMD,
// MAGICGATE, the Memory Stick mark. The room names a device in plain text
// where it has to and draws none of those marks. What a player needs to work
// the device stays: the four shapes on the face buttons, the D-pad's arrows,
// L and R, START, SELECT, HOME, VOL, POWER and HOLD.
//
// Three of the four models are PocketJS's and are not edited here, so a mark
// leaves at load. One that is a material of its own goes with its mesh. One
// that shares a material with legends is found by where its triangles lie,
// and those triangles are dropped. One engraved in the shell itself loses
// its grooves, and the opening is closed with a patch of the shell's own
// material that follows the surface and is shaded as the shell around it.
// The Nintendo 3DS model and the iPod touch model carry no such mark.

import * as THREE from "three";
import type { DeviceId } from "../core/types.ts";

/** A box in the device's frame: millimetres, X right, Y up, Z out of the screen. */
export interface Region {
  x: [number, number];
  y: [number, number];
  z: [number, number];
}

export interface Mark {
  /** What the mark is and where on the device. */
  name: string;
  /** Prefix of the material it is drawn in. */
  material: string;
  /** Its triangles among that material's, by where their centre lies. Absent: the whole material is the mark. */
  within?: Region;
  /**
   * The mark is engraved: grooves sunk into the shell, which is one mesh with
   * it. The shell's triangles that lie wholly inside `within` (the grooves,
   * their walls and the slivers between the letters) are dropped, and a patch
   * closes the opening. The patch follows the shell's outer surface, fitted
   * from the vertices inside `skin` that face `facing` (the floors of the
   * grooves lie under that surface and are left out), `lift` millimetres
   * proud of it, or more where something under it stands higher.
   */
  engraved?: { skin: Region; facing: [number, number, number]; lift: number };
}

const ANY: [number, number] = [-Infinity, Infinity];

export const MARKS: Record<DeviceId, Mark[]> = {
  psp: [
    { name: "PS logo, front", material: "P3D_decals_and_text__Mat.038__0" },
    { name: "PSP wordmark, front", material: "P3D_decals_and_text__Mat.027__0" },
    { name: "Memory Stick mark, front", material: "P3D_decals_and_text__Mat.037__0" },
    // The disc cover's ring and the letters inside it are one material. The
    // letters are inlays: with them gone, the pockets they lay in still spell
    // the words, so each pocket is covered as an engraving is.
    { name: "PSP wordmark, rear", material: "Aluminio", within: { x: [-17.5, 17.5], y: [-4, 4], z: [-12, -7] } },
    {
      name: "PSP wordmark's pockets, rear",
      material: "Carcasa",
      within: { x: [-17.2, 17.8], y: [-3.6, 3.4], z: ANY },
      engraved: { skin: { x: [-19, 19.5], y: [-7, 7], z: [-11, -7] }, facing: [0, 0, -1], lift: 0.05 },
    },
    { name: "UMD mark, rear", material: "Aluminio", within: { x: [-34, -24.5], y: [29, 33.6], z: [-12, -6] } },
    {
      name: "UMD mark's pockets, rear",
      material: "Carcasa",
      within: { x: [-33.6, -24.9], y: [29.5, 33.1], z: ANY },
      engraved: { skin: { x: [-37, -21.5], y: [27, 35.5], z: [-10, -6] }, facing: [0, 0, -1], lift: 0.05 },
    },
    {
      name: "MAGICGATE wordmark, rear",
      material: "Carcasa",
      within: { x: [-84, -72.9], y: [0.2, 2.5], z: ANY },
      engraved: { skin: { x: [-84.4, -71.5], y: [-1.2, 4], z: [-10.5, -6] }, facing: [0, 0, -1], lift: 0.05 },
    },
  ],
  vita: [
    { name: "SONY wordmark, front", material: "PCH-2000 silver legends", within: { x: [-57.5, -43.5], y: [34, 38], z: [6.5, 9] } },
    { name: "PS VITA wordmark, front", material: "PCH-2000 silver legends", within: { x: [-11.5, 11.5], y: [-37.6, -32.4], z: [6.5, 9] } },
    { name: "PS logo, PS button", material: "PCH-2000 silver legends", within: { x: [-74, -67.3], y: [-27, -21.6], z: [6.5, 9] } },
    { name: "PS VITA wordmark, top edge", material: "PCH-2000 silver legends", within: { x: [-6.5, 6.5], y: [42, 45], z: [-1, 5] } },
    { name: "PS logo, rear", material: "PCH-2000 silver legends", within: { x: [-5.5, 5.5], y: [-4.5, 4.5], z: [-10, -6] } },
    { name: "SONY, model number and PlayStation Vita line, rear", material: "PCH-2000 silver legends", within: { x: [-12.5, 12.5], y: [-33.3, -29.9], z: [-10, -6] } },
  ],
  "3ds": [],
  "ipod-touch": [],
  android: [],
};

const inside = (region: Region, point: THREE.Vector3): boolean =>
  point.x >= region.x[0] && point.x <= region.x[1] && point.y >= region.y[0] && point.y <= region.y[1] && point.z >= region.z[0] && point.z <= region.z[1];

/** The marks that sit in a material, by its name. */
export function marksIn(device: DeviceId, material: string): Mark[] {
  return MARKS[device].filter((mark) => material.startsWith(mark.material));
}

/** True when the whole material is a mark: its mesh leaves the model. */
export function isWholly(marks: readonly Mark[]): boolean {
  return marks.some((mark) => !mark.within);
}

/**
 * The corners of the triangles that stay once the marks' triangles are
 * dropped, and how many triangles each mark took. A mark drawn among legends
 * loses the triangles whose centre lies in it. An engraved one loses the
 * triangles that lie in it with all three corners, at the depth of the
 * shell's skin: one that reaches out of it is the shell around the mark and
 * stays. `toDevice` takes the geometry's positions to the device's frame.
 */
export function withoutMarks(
  geometry: THREE.BufferGeometry,
  toDevice: THREE.Matrix4,
  marks: readonly Mark[],
): { kept: number[]; taken: Map<string, number> } {
  const placed = marks.filter((mark) => mark.within);
  const position = geometry.getAttribute("position");
  const index = geometry.getIndex();
  const corners = index ? index.count : position.count;
  const kept: number[] = [];
  const taken = new Map<string, number>();
  const centre = new THREE.Vector3();
  const at = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  const claims = (mark: Mark): boolean => {
    if (!mark.engraved) return inside(mark.within!, centre);
    const depth = { ...mark.within!, z: mark.engraved.skin.z };
    return at.every((point) => inside(depth, point));
  };
  for (let i = 0; i < corners; i += 3) {
    const triangle = [0, 1, 2].map((k) => (index ? index.getX(i + k) : i + k));
    centre.set(0, 0, 0);
    triangle.forEach((vertex, k) => centre.add(at[k]!.fromBufferAttribute(position, vertex).applyMatrix4(toDevice)));
    centre.multiplyScalar(1 / 3);
    const mark = placed.find(claims);
    if (mark) taken.set(mark.name, (taken.get(mark.name) ?? 0) + 1);
    else kept.push(...triangle);
  }
  return { kept, taken };
}

/** Solve the 6 by 6 normal equations of a least-squares fit, in place. */
function solve(a: number[][], b: number[]): number[] {
  const n = b.length;
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) if (Math.abs(a[row]![col]!) > Math.abs(a[pivot]![col]!)) pivot = row;
    [a[col], a[pivot]] = [a[pivot]!, a[col]!];
    [b[col], b[pivot]] = [b[pivot]!, b[col]!];
    const lead = a[col]![col]!;
    if (Math.abs(lead) < 1e-12) throw new Error("the surface around a mark does not determine a fit");
    for (let row = col + 1; row < n; row++) {
      const factor = a[row]![col]! / lead;
      for (let k = col; k < n; k++) a[row]![k]! -= factor * a[col]![k]!;
      b[row]! -= factor * b[col]!;
    }
  }
  const x = new Array<number>(n).fill(0);
  for (let row = n - 1; row >= 0; row--) {
    let sum = b[row]!;
    for (let k = row + 1; k < n; k++) sum -= a[row]![k]! * x[k]!;
    x[row] = sum / a[row]![row]!;
  }
  return x;
}

/**
 * A patch that covers a mark engraved in the shell, in the device's frame.
 *
 * The shell's outer surface around the mark is fitted as z = f(x, y), a
 * quadric. The fit starts from every vertex that faces outward and leaves
 * out, round by round, the ones that lie under it: the floors of the grooves
 * and whatever else is inside the shell. `fit` is the worst distance of a
 * vertex the last round kept from that surface, for the caller to judge.
 *
 * The patch is a grid over `within`. Each of its points takes the shell's
 * own height where the shell's surface is at or outside the fit, and the
 * fit's where it is not: over the opening. Its normals are the shell's own
 * where a point lies on the shell, and across the opening they are spread
 * from those, so the patch is shaded as the shell around it and has no edge
 * to see. The whole patch then stands off by `lift`, or by as much as the
 * highest vertex under it needs.
 */
export function patchFor(geometry: THREE.BufferGeometry, toDevice: THREE.Matrix4, mark: Mark): { patch: THREE.BufferGeometry; fit: number } {
  const { within, engraved } = mark;
  if (!within || !engraved) throw new Error(`${mark.name} is not engraved in the shell`);
  const position = geometry.getAttribute("position");
  const normal = geometry.getAttribute("normal");
  const index = geometry.getIndex();
  const facing = new THREE.Vector3(...engraved.facing);
  // Out of the shell is -Z on the back of a device and +Z on its face.
  const out = Math.sign(facing.z);
  const point = new THREE.Vector3();
  const direction = new THREE.Vector3();
  const terms = (x: number, y: number) => [1, x, y, x * x, x * y, y * y];
  // Positions are taken about the middle of the mark, to keep the sums small.
  const cx = (within.x[0] + within.x[1]) / 2;
  const cy = (within.y[0] + within.y[1]) / 2;
  // x, y about the middle; then z and the normal. Every vertex of the shell is
  // read, the ones of dropped triangles too: between the letters they lie on
  // the surface being fitted.
  let used: number[][] = [];
  for (let i = 0; i < position.count; i++) {
    point.fromBufferAttribute(position, i).applyMatrix4(toDevice);
    if (!inside(engraved.skin, point)) continue;
    direction.fromBufferAttribute(normal, i).transformDirection(toDevice);
    if (direction.dot(facing) < 0.9) continue;
    used.push([point.x - cx, point.y - cy, point.z, direction.x, direction.y, direction.z]);
  }
  /** The quadric through one value of the vertices in use, by least squares. */
  const quadric = (value: number): number[] => {
    const ata = Array.from({ length: 6 }, () => new Array<number>(6).fill(0));
    const atb = new Array<number>(6).fill(0);
    for (const sample of used) {
      const row = terms(sample[0]!, sample[1]!);
      for (let r = 0; r < 6; r++) {
        for (let c = 0; c < 6; c++) ata[r]![c]! += row[r]! * row[c]!;
        atb[r]! += row[r]! * sample[value]!;
      }
    }
    return solve(ata, atb);
  };
  const at = (k: number[], x: number, y: number) => terms(x, y).reduce((sum, term, i) => sum + term * k[i]!, 0);
  let k: number[] = [];
  const height = (x: number, y: number) => at(k, x, y);
  for (const under of [0.3, 0.15, 0.08, 0.05, 0.05, Infinity]) {
    if (used.length < 24) throw new Error(`${mark.name}: ${used.length} vertices of the shell's surface around it, too few to fit`);
    k = quadric(2);
    used = used.filter(([x, y, z]) => (height(x!, y!) - z!) * out <= under);
  }
  let fit = 0;
  for (const [x, y, z] of used) fit = Math.max(fit, Math.abs(z! - height(x!, y!)));
  const slope = [quadric(3), quadric(4), quadric(5)];

  // The shell's triangles that still face outward over the mark, with their corners' normals.
  const faces: Array<{ p: THREE.Vector3[]; n: THREE.Vector3[] }> = [];
  const corners = index ? index.count : position.count;
  const edge1 = new THREE.Vector3();
  const edge2 = new THREE.Vector3();
  for (let i = 0; i < corners; i += 3) {
    const triangle = [0, 1, 2].map((c) => (index ? index.getX(i + c) : i + c));
    const p = triangle.map((vertex) => new THREE.Vector3().fromBufferAttribute(position, vertex).applyMatrix4(toDevice));
    if (Math.max(p[0]!.x, p[1]!.x, p[2]!.x) < within.x[0] || Math.min(p[0]!.x, p[1]!.x, p[2]!.x) > within.x[1]) continue;
    if (Math.max(p[0]!.y, p[1]!.y, p[2]!.y) < within.y[0] || Math.min(p[0]!.y, p[1]!.y, p[2]!.y) > within.y[1]) continue;
    direction.crossVectors(edge1.subVectors(p[1]!, p[0]!), edge2.subVectors(p[2]!, p[0]!)).normalize();
    if (direction.dot(facing) < 0.5) continue;
    faces.push({ p, n: triangle.map((vertex) => new THREE.Vector3().fromBufferAttribute(normal, vertex).transformDirection(toDevice)) });
  }

  const columns = Math.max(2, Math.ceil((within.x[1] - within.x[0]) / 0.5));
  const rows = Math.max(2, Math.ceil((within.y[1] - within.y[0]) / 0.5));
  const positions: number[] = [];
  const normals: number[] = [];
  // Whether a point of the grid lies on the shell: its normal is then the shell's own.
  const onShell: boolean[] = [];
  const fitted = new THREE.Vector3();
  const own = new THREE.Vector3();
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= columns; i++) {
      const x = within.x[0] + ((within.x[1] - within.x[0]) * i) / columns;
      const y = within.y[0] + ((within.y[1] - within.y[0]) * j) / rows;
      const smooth = height(x - cx, y - cy);
      let z = smooth;
      fitted.set(at(slope[0]!, x - cx, y - cy), at(slope[1]!, x - cx, y - cy), at(slope[2]!, x - cx, y - cy)).normalize();
      direction.copy(fitted);
      let shell = false;
      for (const face of faces) {
        const [a, b, c] = face.p as [THREE.Vector3, THREE.Vector3, THREE.Vector3];
        const area = (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
        if (Math.abs(area) < 1e-9) continue;
        const v = ((x - a.x) * (c.y - a.y) - (c.x - a.x) * (y - a.y)) / area;
        const w = ((b.x - a.x) * (y - a.y) - (x - a.x) * (b.y - a.y)) / area;
        const u = 1 - v - w;
        if (u < -1e-6 || v < -1e-6 || w < -1e-6) continue;
        const here = u * a.z + v * b.z + w * c.z;
        // A face well under the fit is the floor of a groove or the inside of the shell, not its surface.
        if ((here - smooth) * out < -0.1) continue;
        own.set(0, 0, 0).addScaledVector(face.n[0]!, u).addScaledVector(face.n[1]!, v).addScaledVector(face.n[2]!, w).normalize();
        if (own.dot(fitted) > 0.96) {
          direction.copy(own);
          shell = true;
        }
        if ((here - z) * out > 0) z = here;
      }
      positions.push(x, y, z);
      normals.push(direction.x, direction.y, direction.z);
      onShell.push(shell);
    }
  }
  // Across the opening the normals run from the shell at one edge to the shell at the other: each point
  // off the shell takes the mean of its neighbours, again and again, so no edge of the patch shows.
  if (onShell.some(Boolean)) {
    const stride = columns + 1;
    for (let round = 0; round < 400; round++) {
      for (let j = 0; j <= rows; j++) {
        for (let i = 0; i <= columns; i++) {
          const here = j * stride + i;
          if (onShell[here]) continue;
          let nx = 0, ny = 0, nz = 0, count = 0;
          for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
            const col = i + di, row = j + dj;
            if (col < 0 || col > columns || row < 0 || row > rows) continue;
            const other = (row * stride + col) * 3;
            nx += normals[other]!; ny += normals[other + 1]!; nz += normals[other + 2]!;
            count++;
          }
          normals[here * 3] = nx / count; normals[here * 3 + 1] = ny / count; normals[here * 3 + 2] = nz / count;
        }
      }
    }
    for (let i = 0; i < normals.length; i += 3) {
      direction.set(normals[i]!, normals[i + 1]!, normals[i + 2]!).normalize();
      normals[i] = direction.x; normals[i + 1] = direction.y; normals[i + 2] = direction.z;
    }
  }
  // A rim or a burr of the mark may stand off the surface between two points of the grid: the patch clears the highest.
  const patchAt = (x: number, y: number): number => {
    const gx = Math.min(columns - 1e-9, Math.max(0, ((x - within.x[0]) / (within.x[1] - within.x[0])) * columns));
    const gy = Math.min(rows - 1e-9, Math.max(0, ((y - within.y[0]) / (within.y[1] - within.y[0])) * rows));
    const i = Math.floor(gx);
    const j = Math.floor(gy);
    const zAt = (col: number, row: number) => positions[(row * (columns + 1) + col) * 3 + 2]!;
    const s = gx - i;
    const t = gy - j;
    return (zAt(i, j) * (1 - s) + zAt(i + 1, j) * s) * (1 - t) + (zAt(i, j + 1) * (1 - s) + zAt(i + 1, j + 1) * s) * t;
  };
  // Only what the shell still draws counts: the grooves' own vertices are gone with their triangles.
  const drawn = new Set<number>();
  for (let i = 0; i < (index ? index.count : position.count); i++) drawn.add(index ? index.getX(i) : i);
  let highest = 0;
  for (const i of drawn) {
    point.fromBufferAttribute(position, i).applyMatrix4(toDevice);
    if (point.x < within.x[0] || point.x > within.x[1] || point.y < within.y[0] || point.y > within.y[1]) continue;
    if (point.z < engraved.skin.z[0] || point.z > engraved.skin.z[1]) continue;
    highest = Math.max(highest, (point.z - patchAt(point.x, point.y)) * out);
  }
  const proud = out * Math.max(engraved.lift, highest + 0.03);
  for (let i = 2; i < positions.length; i += 3) positions[i]! += proud;

  const order: number[] = [];
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < columns; i++) {
      const a = j * (columns + 1) + i;
      const b = a + 1;
      const c = a + columns + 1;
      const d = c + 1;
      // Counter-clockwise seen from the side the shell faces.
      if (facing.z < 0) order.push(a, c, b, b, c, d);
      else order.push(a, b, c, b, d, c);
    }
  }
  const patch = new THREE.BufferGeometry();
  patch.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  patch.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  patch.setIndex(order);
  return { patch, fit };
}
